import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useCrossroadsEntityGrid } from '../../../../api/hooks/useCrossroadsEntityGrid'
import { fetchClient } from '../../../../api/typedClient'
import { AlignedRow, EntityData } from '../types'
import { getApiType } from '../utils'
import { alignRowsAcrossGrids } from '../utils/dataAlignment'

interface TenantMapping extends EntityData {
  type: string
  goids: string[]
  display_name: string
  source_id: string
  matching_info: Record<string, unknown>
  extra_data: Record<string, unknown>
  _id: string
  source_system: string
  children: Record<string, unknown>
}

const MAPPINGS_FETCH_LIMIT = 10000

interface UseThreeWayDataProps {
  entityType: string
  leftTenant: string
  rightTenant: string
}

interface UseThreeWayDataReturn {
  mrdData: EntityData[] | null
  mrdLoading: boolean
  leftMappings: TenantMapping[]
  leftLoading: boolean
  rightMappings: TenantMapping[]
  rightLoading: boolean
  alignedRows: AlignedRow[]
  isLoading: boolean
}

/**
 * Hook for fetching and aligning three-way mapping data
 */
export const useThreeWayData = ({
  entityType,
  leftTenant,
  rightTenant,
}: UseThreeWayDataProps): UseThreeWayDataReturn => {
  // Check if we're dealing with companies
  const isCompanyType =
    entityType === 'company' || entityType === 'counterparty'

  // Fetch MRD data using grid endpoint for most entities
  const { data: gridData, isLoading: gridLoading } = useCrossroadsEntityGrid(
    entityType,
    !isCompanyType, // Disable for companies
  )

  // Fetch companies data directly for company/counterparty types
  const { data: companiesData, isLoading: companiesLoading } = useQuery({
    queryKey: ['crossroads-network-companies-3way'],
    queryFn: async () => {
      const { data, error } = await fetchClient.GET(
        '/crossroads/network/companies/',
        {
          headers: {
            'X-Tenant-Name': 'Gravitate',
          },
        },
      )
      if (error) {
        console.error('Failed to fetch companies:', error)
        return { rows: [], column_defs: [] }
      }
      // Transform to match grid data format with rows
      return {
        rows: data || [],
        column_defs: [], // Will be generated in the component
      }
    },
    enabled: isCompanyType,
    staleTime: 60000,
  })

  // Use appropriate data based on entity type
  const mrdData = isCompanyType ? companiesData : gridData
  const mrdLoading = isCompanyType ? companiesLoading : gridLoading

  // Fetch left tenant mappings
  const { data: leftMappings = [], isLoading: leftLoading } = useQuery<
    TenantMapping[]
  >({
    queryKey: ['tenant-mappings', leftTenant, entityType],
    queryFn: async () => {
      if (leftTenant === 'none') return []
      try {
        const { data, error } = await fetchClient.GET('/crossroads/mappings/', {
          params: {
            query: {
              type: getApiType(entityType),
              limit: MAPPINGS_FETCH_LIMIT,
            },
          },
          headers: { 'X-Tenant-Name': leftTenant },
        })
        if (error) {
          console.error(`Failed to fetch mappings for ${leftTenant}:`, error)
          return []
        }
        return Array.isArray(data) ? (data as TenantMapping[]) : []
      } catch (error) {
        console.error(`Failed to fetch mappings for ${leftTenant}:`, error)
        return []
      }
    },
    enabled: leftTenant !== 'none',
    staleTime: 30000,
  })

  // Fetch right tenant mappings
  const { data: rightMappings = [], isLoading: rightLoading } = useQuery<
    TenantMapping[]
  >({
    queryKey: ['tenant-mappings', rightTenant, entityType],
    queryFn: async () => {
      if (rightTenant === 'none') return []
      try {
        const { data, error } = await fetchClient.GET('/crossroads/mappings/', {
          params: {
            query: {
              type: getApiType(entityType),
              limit: MAPPINGS_FETCH_LIMIT,
            },
          },
          headers: { 'X-Tenant-Name': rightTenant },
        })
        if (error) {
          console.error(`Failed to fetch mappings for ${rightTenant}:`, error)
          return []
        }
        return Array.isArray(data) ? (data as TenantMapping[]) : []
      } catch (error) {
        console.error(`Failed to fetch mappings for ${rightTenant}:`, error)
        return []
      }
    },
    enabled: rightTenant !== 'none',
    staleTime: 30000,
  })

  // Align rows across all three grids
  const alignedRows = useMemo<AlignedRow[]>(() => {
    try {
      const result = alignRowsAcrossGrids(mrdData, leftMappings, rightMappings)
      return Array.isArray(result) ? result : []
    } catch (error) {
      console.error('❌ Error during data alignment:', error)
      return []
    }
  }, [
    mrdData,
    leftMappings,
    rightMappings,
    entityType,
    leftTenant,
    rightTenant,
  ])

  const isLoading = mrdLoading || leftLoading || rightLoading

  return {
    mrdData,
    mrdLoading,
    leftMappings,
    leftLoading,
    rightMappings,
    rightLoading,
    alignedRows,
    isLoading,
  }
}
